import type { z } from "zod/v4";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import { buildSeriesLabelMap } from "@/lib/ai/visualization/views/shared/label-map";
import { resolveRankOnlyAxisDirections } from "@/lib/ai/visualization/views/shared/presentation-defaults";
import { buildSeriesEntries } from "@/lib/ai/visualization/views/shared/series-entries";
import {
  buildSpecValidationIssue,
  pushDuplicateValueIssues,
  pushMappedKeyIssues,
  pushRequiredMappedKeyCoverageIssues,
  pushRequiredTextIssue,
  pushSelectedKeyIssues,
  validateFieldRole,
} from "@/lib/ai/visualization/views/shared/spec-validation";
import type {
  ValidateVizSpecArgs,
  VizAuthoringCardDraft,
} from "@/lib/ai/visualization/views/shared/view-definition";
import type { timeSeriesComposedAuthoringSchema } from "@/lib/ai/visualization/views/time-series-composed/schema";

type TimeSeriesComposedSpec = Extract<
  VizSpec,
  { type: "time-series-composed" }
>;
type TimeSeriesComposedAuthoring = z.infer<
  typeof timeSeriesComposedAuthoringSchema
>;

/**
 * composed chart の series binding と presentation prop の整合を検証する。
 *
 * @remarks
 * line/bar の混在は authoring schema だけでは dataset role まで分からないため、
 * compiled spec を dataset と突き合わせて fail-fast する。
 */
export function validateTimeSeriesComposedSpec(
  args: ValidateVizSpecArgs<TimeSeriesComposedSpec>,
): VizAuthoringIssue[] {
  const issues: VizAuthoringIssue[] = [];
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.xKey",
    fieldName: args.spec.xKey,
    role: "time-dimension",
  });

  pushDuplicateValueIssues({
    issues,
    path: "spec.lineSeriesKeys",
    values: args.spec.lineSeriesKeys,
  });
  pushDuplicateValueIssues({
    issues,
    path: "spec.barSeriesKeys",
    values: args.spec.barSeriesKeys,
  });
  pushDuplicateValueIssues({
    issues,
    path: "spec.(lineSeriesKeys|barSeriesKeys)",
    values: [...args.spec.lineSeriesKeys, ...args.spec.barSeriesKeys],
  });

  args.spec.lineSeriesKeys.forEach((seriesKey, index) => {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: `spec.lineSeriesKeys[${index}]`,
      fieldName: seriesKey,
      role: "metric",
    });
  });
  args.spec.barSeriesKeys.forEach((seriesKey, index) => {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: `spec.barSeriesKeys[${index}]`,
      fieldName: seriesKey,
      role: "metric",
    });
  });

  if (
    args.spec.lineSeriesKeys.length === 0 &&
    args.spec.barSeriesKeys.length === 0
  ) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.(lineSeriesKeys|barSeriesKeys)",
        code: "missing-series-keys",
        message:
          "must include at least one metric key in spec.lineSeriesKeys or spec.barSeriesKeys for a renderer-ready spec",
      }),
    );
  }

  const allowedSeriesKeys = new Set([
    ...args.spec.lineSeriesKeys,
    ...args.spec.barSeriesKeys,
  ]);
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.xLabel",
    value: args.spec.componentProps?.xLabel ?? null,
    message:
      "xLabel is required because chart axis labels are owned by the authoring output",
  });
  pushMappedKeyIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    allowedKeys: allowedSeriesKeys,
    emptyAllowedMessage:
      "seriesLabels requires at least one metric key in spec.lineSeriesKeys/spec.barSeriesKeys",
  });
  pushRequiredMappedKeyCoverageIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    requiredKeys: [...args.spec.lineSeriesKeys, ...args.spec.barSeriesKeys],
    emptyMessage:
      "seriesLabels must include every selected metric key because legend labels are owned by the authoring output",
  });
  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.defaultVisibleSeriesKeys",
    selectedKeys: args.spec.componentProps?.defaultVisibleSeriesKeys ?? [],
    allowedKeys: allowedSeriesKeys,
    emptyAllowedMessage:
      "defaultVisibleSeriesKeys requires at least one metric key in spec.lineSeriesKeys/spec.barSeriesKeys",
  });

  return issues;
}

/**
 * structured authoring payload を `time-series-composed` 用の renderer-ready spec へ変換する。
 */
export function compileTimeSeriesComposedSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<TimeSeriesComposedSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: TimeSeriesComposedAuthoring;
}): TimeSeriesComposedSpec {
  const axisDirections = resolveRankOnlyAxisDirections(args.dataset, [
    ...args.view.lineSeriesKeys,
    ...args.view.barSeriesKeys,
  ]);

  return {
    type: "time-series-composed",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    xKey: args.view.xKey,
    lineSeriesKeys: args.view.lineSeriesKeys,
    barSeriesKeys: args.view.barSeriesKeys,
    stacking: args.view.stacking ?? "grouped",
    componentProps: {
      xLabel: args.view.xLabel,
      ...(axisDirections ? { axisDirections } : null),
      seriesLabels: buildSeriesLabelMap({
        seriesKeys: [...args.view.lineSeriesKeys, ...args.view.barSeriesKeys],
        entries: args.view.seriesLabelEntries,
      }),
    },
  };
}

/**
 * time-series-composed 用の UI element を構築する。
 */
export function buildTimeSeriesComposedElement(
  args: BuildVizElementArgs<TimeSeriesComposedSpec>,
): BuiltVizElement {
  const seriesLabels = args.spec.componentProps?.seriesLabels ?? {};

  return {
    elementKey: args.chartKey,
    element: {
      type: "TimeSeriesComposed",
      props: {
        dataPath: args.dataPath,
        xKey: args.spec.xKey,
        lineSeries: buildSeriesEntries(
          args.dataset,
          args.spec.lineSeriesKeys,
          seriesLabels,
        ),
        barSeries: buildSeriesEntries(
          args.dataset,
          args.spec.barSeriesKeys,
          seriesLabels,
        ),
        stacking: args.spec.stacking,
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
